/**
 * La cortina que se pone al recuperar un proyecto.
 *
 * Tercera de la familia, con la escenografía de `ProjectSwitchOverlay` y el
 * mismo relevo del final que la de eliminar. Aquí la simetría es lo que la
 * justifica: borrar tuvo su ceremonia, y recuperar es deshacerla — verlo pasar
 * con la misma forma es lo que cierra el círculo en vez de dejar el proyecto
 * apareciendo de golpe como si nunca hubiera pasado nada.
 *
 * ── Qué hacen esos segundos ─────────────────────────────────────────────────
 *
 * NO son una espera fabricada. La petición corre desde el primer fotograma; lo
 * que hay es un mínimo, para que la escena no aparezca y desaparezca en un
 * parpadeo cuando el servidor contesta en trescientos milisegundos. Detrás,
 * además, el panel está recargando su lista de proyectos y el taller entero se
 * recompone: la cortina tapa exactamente ese hueco.
 *
 * A diferencia de la de eliminar, aquí no hay botón de cancelar. Recuperar no
 * destruye nada, así que no hay de qué arrepentirse.
 */
import { useEffect, useRef, useState } from 'react'
import { LoadingMascot } from '@webbuilder/ui'
import { HOLD_MS, RELAY_MS } from './ProjectSwitchOverlay'
import { translate, useI18n } from '../lib/i18n'

/** Lo que dura como mínimo el «Recuperando…», para que se lea. */
const RESTORE_MIN_MS = 5_000

type Phase = 'restoring' | 'done' | 'relay'

function label(name: string): string {
  return name.trim() || translate('deleteOverlay.noName')
}

export function ProjectRestoreOverlay({ projectName, onRestore, onSwap, onDone, onFailed }: {
  projectName: string
  /** Recupera de verdad. Devuelve si salió bien. */
  onRestore: () => Promise<boolean>
  /** Abrir el proyecto recuperado. Se llama UNA vez, a mitad del relevo. */
  onSwap: () => void
  onDone: () => void
  onFailed: () => void
}) {
  const { t } = useI18n()
  const [phase, setPhase] = useState<Phase>('restoring')

  /* En referencias y fuera de las dependencias, por lo mismo que en sus dos
     hermanas: son funciones nuevas en cada render de quien nos monta, y con
     ellas en la lista el guion se reiniciaría en cada repintado. */
  const callbacks = useRef({ onRestore, onSwap, onDone, onFailed })
  callbacks.current = { onRestore, onSwap, onDone, onFailed }

  useEffect(() => {
    if (phase !== 'restoring') return
    let alive = true
    /* La petición y el mínimo, a la vez y no en serie: si el servidor tarda más
       que el mínimo, manda el servidor; si tarda menos, manda el mínimo. Se
       espera lo que dure el más lento de los dos, nunca la suma. */
    void Promise.all([
      callbacks.current.onRestore(),
      new Promise((resolve) => window.setTimeout(resolve, RESTORE_MIN_MS)),
    ]).then(([ok]) => {
      if (!alive) return
      if (ok) setPhase('done')
      else callbacks.current.onFailed()
    })
    return () => { alive = false }
  }, [phase])

  useEffect(() => {
    if (phase !== 'done') return
    const toRelay = window.setTimeout(() => { setPhase('relay') }, HOLD_MS)
    return () => { window.clearTimeout(toRelay) }
  }, [phase])

  useEffect(() => {
    if (phase !== 'relay') return
    callbacks.current.onSwap()
    const end = window.setTimeout(() => { callbacks.current.onDone() }, RELAY_MS + HOLD_MS)
    return () => { window.clearTimeout(end) }
  }, [phase])

  const arrived = phase === 'relay'

  return (
    <div
      className="dash-project-switch dash-project-restore"
      role="dialog"
      aria-modal="true"
      aria-label={t('restoreOverlay.dialogLabel', { name: label(projectName) })}
    >
      <p className="dash-sr-only" role="status" aria-live="polite">
        {phase === 'restoring' ? t('restoreOverlay.srRestoring')
          : phase === 'done' ? t('restoreOverlay.srDone')
            : t('restoreOverlay.srProject', { name: label(projectName) })}
      </p>

      <div className="dash-project-switch-stage">
        <span className={`dash-project-switch-mascot${arrived ? ' is-relay' : ''}`}>
          <LoadingMascot spinning={phase !== 'done'} size={76} restWhenIdle />
        </span>

        <div className="dash-project-switch-plate">
          <span className={`dash-project-switch-name${arrived ? ' is-in' : ''}`}>
            {label(projectName)}
          </span>
        </div>

        <p className={`dash-project-switch-role${arrived ? ' is-in' : ''}`}>
          {phase === 'restoring' ? t('restoreOverlay.restoring')
            : phase === 'done' ? t('restoreOverlay.done')
              : t('restoreOverlay.opening')}
        </p>
      </div>
    </div>
  )
}
